"use client";

// Gmail REST calls made directly from the browser with a bearer access token
// (no server proxy — this site is a static export). Same pattern Google's own
// JS client libraries use: fetch + Authorization header, CORS-enabled by Google.

import type { EmailDetail, InboxProfile, NormalizedEmail } from "./types";

const GMAIL_BASE = "https://gmail.googleapis.com/gmail/v1/users/me";

type GmailHeader = { name: string; value: string };
type GmailPart = {
  mimeType?: string;
  filename?: string;
  body?: { data?: string; attachmentId?: string; size?: number };
  parts?: GmailPart[];
  headers?: GmailHeader[];
};
type GmailMessageRaw = {
  id: string;
  internalDate?: string;
  labelIds?: string[];
  sizeEstimate?: number;
  snippet?: string;
  payload?: GmailPart;
};
type GmailListRaw = { messages?: { id: string }[] };
type GmailProfileRaw = { emailAddress?: string; messagesTotal?: number; threadsTotal?: number };

async function gmailFetch<T>(accessToken: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${GMAIL_BASE}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${accessToken}`, ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Gmail API error (${res.status}): ${text || res.statusText}`);
  }
  if (res.status === 204) return null as T;
  return res.json();
}

export async function fetchInboxProfile(accessToken: string): Promise<InboxProfile> {
  const data = await gmailFetch<GmailProfileRaw>(accessToken, "/profile");
  return {
    emailAddress: data.emailAddress ?? "",
    messagesTotal: data.messagesTotal ?? 0,
    threadsTotal: data.threadsTotal ?? 0,
  };
}

function headerValue(headers: GmailHeader[] | undefined, name: string): string {
  return headers?.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value ?? "";
}

export async function listAndFetchMessages(
  accessToken: string,
  accountEmail: string,
  query: string,
  maxResults: number,
): Promise<NormalizedEmail[]> {
  const q = query ? `&q=${encodeURIComponent(query)}` : "";
  const list = await gmailFetch<GmailListRaw>(accessToken, `/messages?maxResults=${maxResults}${q}`);
  const ids: string[] = (list.messages ?? []).map((m) => m.id);

  const fields = encodeURIComponent("id,internalDate,labelIds,sizeEstimate,snippet,payload/headers");
  const messages = await Promise.all(
    ids.map((id) =>
      gmailFetch<GmailMessageRaw>(
        accessToken,
        `/messages/${id}?format=metadata&metadataHeaders=From&metadataHeaders=Subject&metadataHeaders=Date&fields=${fields}`,
      ).catch(() => null),
    ),
  );

  return messages
    .filter((m): m is NonNullable<typeof m> => m !== null)
    .map((m) => {
      const labels: string[] = m.labelIds ?? [];
      return {
        id: m.id,
        account_id: accountEmail,
        subject: headerValue(m.payload?.headers, "Subject") || "(no subject)",
        from: headerValue(m.payload?.headers, "From"),
        snippet: m.snippet ?? "",
        received_at: m.internalDate ? new Date(Number(m.internalDate)).toISOString() : "",
        is_read: !labels.includes("UNREAD"),
        size_bytes: m.sizeEstimate ?? 0,
        has_attachment: false,
        labels,
      };
    });
}

function decodeBase64Url(data: string): string {
  const base64 = data.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder("utf-8").decode(bytes);
}

function stripHtml(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function extractBody(payload: GmailPart): string {
  const plainParts: string[] = [];
  const htmlParts: string[] = [];

  function walk(part: GmailPart) {
    if (part.body?.data && part.mimeType === "text/plain") {
      plainParts.push(decodeBase64Url(part.body.data));
    } else if (part.body?.data && part.mimeType === "text/html") {
      htmlParts.push(decodeBase64Url(part.body.data));
    }
    part.parts?.forEach(walk);
  }
  walk(payload);

  if (plainParts.length) return plainParts.join("\n\n").slice(0, 12000);
  if (htmlParts.length) return stripHtml(htmlParts.join("\n\n")).slice(0, 12000);
  return "";
}

export async function fetchMessageDetail(accessToken: string, id: string): Promise<EmailDetail> {
  const m = await gmailFetch<GmailMessageRaw>(accessToken, `/messages/${id}?format=full`);
  return {
    id: m.id,
    from: headerValue(m.payload?.headers, "From"),
    subject: headerValue(m.payload?.headers, "Subject") || "(no subject)",
    date: headerValue(m.payload?.headers, "Date"),
    body: extractBody(m.payload ?? {}) || m.snippet || "",
  };
}

export async function performMessageAction(
  accessToken: string,
  id: string,
  action: "trash" | "archive" | "markRead",
): Promise<void> {
  if (action === "trash") {
    await gmailFetch(accessToken, `/messages/${id}/trash`, { method: "POST" });
    return;
  }
  const removeLabelIds = action === "archive" ? ["INBOX"] : ["UNREAD"];
  await gmailFetch(accessToken, `/messages/${id}/modify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ removeLabelIds }),
  });
}

/** Real match count for a query. Gmail's `resultSizeEstimate` field is a rough
 *  internal estimate, not an accurate count — it commonly plateaus at the same
 *  value (e.g. 201) regardless of the actual query, especially with a small
 *  maxResults. Count for real by paginating id-only pages (cheap — no
 *  per-message hydration), capped so a huge match can't run away. Matches the
 *  mobile app's own GmailDataSource.countMatching exactly. */
export async function countMatching(accessToken: string, query: string): Promise<number> {
  const ids = await listMessageIdsPaged(accessToken, query, 5000);
  return ids.length;
}

/** Message ids only (no metadata fetch) for a query, paginated up to [cap] —
 *  used for bulk actions where hydrating full messages would be too slow. */
export async function listMessageIdsPaged(accessToken: string, query: string, cap = 1000): Promise<string[]> {
  const ids: string[] = [];
  let pageToken: string | undefined;
  const q = query ? `q=${encodeURIComponent(query)}&` : "";
  do {
    const pageParam = pageToken ? `pageToken=${encodeURIComponent(pageToken)}&` : "";
    const data = await gmailFetch<{ messages?: { id: string }[]; nextPageToken?: string }>(
      accessToken,
      `/messages?${q}${pageParam}maxResults=${Math.min(500, cap - ids.length)}`,
    );
    ids.push(...(data.messages ?? []).map((m) => m.id));
    pageToken = data.nextPageToken;
  } while (pageToken && ids.length < cap);
  return ids.slice(0, cap);
}

let labelCache: Map<string, string> | null = null;

async function getOrCreateLabelId(accessToken: string, labelName: string): Promise<string> {
  if (!labelCache) {
    const data = await gmailFetch<{ labels?: { id: string; name: string }[] }>(accessToken, "/labels");
    labelCache = new Map((data.labels ?? []).map((l) => [l.name.toLowerCase(), l.id]));
  }
  const existing = labelCache.get(labelName.toLowerCase());
  if (existing) return existing;
  const created = await gmailFetch<{ id: string }>(accessToken, "/labels", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: labelName, labelListVisibility: "labelShow", messageListVisibility: "show" }),
  });
  labelCache.set(labelName.toLowerCase(), created.id);
  return created.id;
}

export type MailAction =
  | "archive"
  | "trash"
  | "restore"
  | "markRead"
  | "markUnread"
  | "star"
  | "unstar"
  | "moveToLabel";

/** Applies one mailbox action to a single message id — the primitive every
 *  bulk-action flow (Explore Suggestions, Smart Cleanup, Rules, Sender
 *  Manager, Stop Unwanted Mail) is built on. */
export async function applyMailAction(
  accessToken: string,
  id: string,
  action: MailAction,
  labelName?: string,
): Promise<void> {
  switch (action) {
    case "archive":
      await gmailFetch(accessToken, `/messages/${id}/modify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ removeLabelIds: ["INBOX"] }),
      });
      return;
    case "trash":
      await gmailFetch(accessToken, `/messages/${id}/trash`, { method: "POST" });
      return;
    case "restore":
      await gmailFetch(accessToken, `/messages/${id}/untrash`, { method: "POST" });
      return;
    case "markRead":
      await gmailFetch(accessToken, `/messages/${id}/modify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ removeLabelIds: ["UNREAD"] }),
      });
      return;
    case "markUnread":
      await gmailFetch(accessToken, `/messages/${id}/modify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addLabelIds: ["UNREAD"] }),
      });
      return;
    case "star":
      await gmailFetch(accessToken, `/messages/${id}/modify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addLabelIds: ["STARRED"] }),
      });
      return;
    case "unstar":
      await gmailFetch(accessToken, `/messages/${id}/modify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ removeLabelIds: ["STARRED"] }),
      });
      return;
    case "moveToLabel": {
      if (!labelName) return;
      const labelId = await getOrCreateLabelId(accessToken, labelName);
      await gmailFetch(accessToken, `/messages/${id}/modify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addLabelIds: [labelId], removeLabelIds: ["INBOX"] }),
      });
      return;
    }
  }
}

/** A small, cheap sample of recent messages across the mailbox (or matching
 *  [query]) as NormalizedEmail — the building block for every "sample N
 *  recent messages and aggregate by sender" screen (Explore Suggestions,
 *  Inbox Overview, Sender Manager, Subscriptions, Spending, Habits). */
export async function listSample(
  accessToken: string,
  accountEmail: string,
  query: string,
  maxResults: number,
): Promise<NormalizedEmail[]> {
  return listAndFetchMessages(accessToken, accountEmail, query, maxResults);
}

export type GmailAttachmentMeta = {
  messageId: string;
  attachmentId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  fromName: string;
  receivedAt: string;
};

function collectAttachmentParts(part: GmailPart, out: { attachmentId: string; filename: string; mimeType: string; sizeBytes: number }[]) {
  const filename = part.filename;
  const attachmentId = part.body?.attachmentId;
  if (filename && attachmentId) {
    out.push({
      attachmentId,
      filename,
      mimeType: part.mimeType ?? "application/octet-stream",
      sizeBytes: part.body?.size ?? 0,
    });
  }
  part.parts?.forEach((p) => collectAttachmentParts(p, out));
}

/** Fetches full messages matching [query] (has:attachment by convention)
 *  and flattens every attachment part found across them. */
export async function listAttachmentsForQuery(
  accessToken: string,
  query: string,
  maxResults: number,
): Promise<GmailAttachmentMeta[]> {
  const list = await gmailFetch<GmailListRaw>(
    accessToken,
    `/messages?maxResults=${maxResults}&q=${encodeURIComponent(query)}`,
  );
  const ids = (list.messages ?? []).map((m) => m.id);
  const messages = await Promise.all(
    ids.map((id) => gmailFetch<GmailMessageRaw>(accessToken, `/messages/${id}?format=full`).catch(() => null)),
  );

  const refs: GmailAttachmentMeta[] = [];
  for (const m of messages) {
    if (!m?.payload) continue;
    const parts: { attachmentId: string; filename: string; mimeType: string; sizeBytes: number }[] = [];
    collectAttachmentParts(m.payload, parts);
    const fromName = headerValue(m.payload.headers, "From");
    const receivedAt = m.internalDate ? new Date(Number(m.internalDate)).toISOString() : "";
    for (const p of parts) {
      refs.push({ messageId: m.id, fromName, receivedAt, ...p });
    }
  }
  return refs;
}

/** Downloads a single attachment's bytes and returns a Blob URL, for
 *  triggering a browser download or preview. */
export async function downloadAttachment(
  accessToken: string,
  messageId: string,
  attachmentId: string,
  mimeType: string,
): Promise<string> {
  const data = await gmailFetch<{ data?: string }>(
    accessToken,
    `/messages/${messageId}/attachments/${attachmentId}`,
  );
  const base64 = (data.data ?? "").replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}
